import { startTransition, useCallback, useEffect, useRef, useState } from 'react';
import { useTokensContext } from '../..';
import { useIsRTL } from '../../../../locale';
const focusOnElement = (element, options) => {
  if (document.activeElement === element) return;
  element?.focus(options);
};
export const useTokenFocusState = ({
  index = 0,
  tabIndex,
  isPopoverOpen,
  preventScrollOnFocus,
  textRef,
  closeRef
}) => {
  const isRTL = useIsRTL();
  const [tokenHasFocus, setTokenHasFocus] = useState(false);
  const [defaultFocusColumnIndex, setDefaultFocusColumnIndex] = useState(0);
  const defaultOnUpdateFocus = useCallback((tokenIndex, columnIndex) => {
    // This function will only be called when the token is standalone. That is, no `TokensContext` is found.
    // So only move focus between columns in the current token, and ignore the `tokenIndex` change.
    if (tokenIndex === index && columnIndex !== undefined) {
      setDefaultFocusColumnIndex(columnIndex);
    }
    setTokenHasFocus(true);
  }, [index]);
  const tokensContext = useTokensContext();
  const {
    focusTokenIndex,
    focusColumnIndex,
    onUpdateFocus
  } = tokensContext ?? {
    focusTokenIndex: tokenHasFocus ? index : undefined,
    focusColumnIndex: defaultFocusColumnIndex,
    onUpdateFocus: defaultOnUpdateFocus
  };
  const lastFocusedIndexRef = useRef(0);
  if (focusTokenIndex !== undefined) {
    lastFocusedIndexRef.current = focusTokenIndex;
  }
  const onBlur = e => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      if (isPopoverOpen) {
        e.stopPropagation();
        return;
      }
      if (tokensContext) return;
      startTransition(() => {
        setTokenHasFocus(false);
      });
    }
  };
  const isFocusable = tabIndex === 0 || lastFocusedIndexRef.current === index;
  const focusableColumnIndex = isFocusable ? focusColumnIndex : -1;
  useEffect(() => {
    if (isPopoverOpen || focusTokenIndex === undefined || focusTokenIndex !== index) return;
    if (focusColumnIndex === 1) {
      if (closeRef.current) {
        focusOnElement(closeRef.current, {
          preventScroll: preventScrollOnFocus
        });
      } else {
        // If no close button is present, move the focus to the text column (columnIndex=0)
        onUpdateFocus(index, 0);
      }
    } else {
      focusOnElement(textRef.current, {
        preventScroll: preventScrollOnFocus
      });
    }
  }, [closeRef, focusColumnIndex, focusTokenIndex, index, isPopoverOpen, onUpdateFocus, preventScrollOnFocus, textRef]);
  const onKeyDown = e => {
    switch (e.key) {
      case 'ArrowUp':
        e.preventDefault();
        onUpdateFocus(index - 1);
        break;
      case 'ArrowDown':
        e.preventDefault();
        onUpdateFocus(index + 1);
        break;
      case isRTL ? 'ArrowLeft' : 'ArrowRight':
        e.preventDefault();
        if (focusColumnIndex === 0 && closeRef.current) {
          // Move the focus from the first column to the second one
          onUpdateFocus(index, 1);
        } else {
          // Move the focus to the first column of the next token
          onUpdateFocus(index + 1, 0);
        }
        break;
      case isRTL ? 'ArrowRight' : 'ArrowLeft':
        e.preventDefault();
        if (focusColumnIndex === 1) {
          // Move the focus from the second column to the first one
          onUpdateFocus(index, 0);
        } else {
          // Move the focus to the second column of the previous token
          onUpdateFocus(index - 1, 1);
        }
        break;
      case 'Home':
        e.preventDefault();
        if (e.ctrlKey) {
          // Move the focus to the first column of the first token
          onUpdateFocus(0, 0);
        } else {
          // Move the focus to the first column of the current token
          onUpdateFocus(index, 0);
        }
        break;
      case 'End':
        e.preventDefault();
        if (e.ctrlKey) {
          // Move the focus to the second column of the last token
          onUpdateFocus(Number.MAX_SAFE_INTEGER, 1);
        } else {
          // Move the focus to the second column of the current token
          onUpdateFocus(index, 1);
        }
        break;
      default:
        return;
    }
  };
  const onClick = e => {
    const isClickingClose = e.target instanceof HTMLElement && closeRef.current?.contains(e.target);
    onUpdateFocus(index, isClickingClose ? 1 : 0);
  };
  const onFocus = e => {
    const isFocusingClose = e.target instanceof HTMLElement && closeRef.current?.contains(e.target);
    onUpdateFocus(index, isFocusingClose ? 1 : 0);
  };
  return {
    onKeyDown,
    onClick,
    onBlur,
    onFocus,
    focusableColumnIndex
  };
};
//# sourceMappingURL=useTokenFocusState.js.map